var __addDisposableResource = (this && this.__addDisposableResource) || function (env, value, async) {
    if (value !== null && value !== void 0) {
        if (typeof value !== "object" && typeof value !== "function") throw new TypeError("Object expected.");
        var dispose, inner;
        if (async) {
            if (!Symbol.asyncDispose) throw new TypeError("Symbol.asyncDispose is not defined.");
            dispose = value[Symbol.asyncDispose];
        }
        if (dispose === void 0) {
            if (!Symbol.dispose) throw new TypeError("Symbol.dispose is not defined.");
            dispose = value[Symbol.dispose];
            if (async) inner = dispose;
        }
        if (typeof dispose !== "function") throw new TypeError("Object not disposable.");
        if (inner) dispose = function() { try { inner.call(this); } catch (e) { return Promise.reject(e); } };
        env.stack.push({ value: value, dispose: dispose, async: async });
    }
    else if (async) {
        env.stack.push({ async: true });
    }
    return value;
};
var __disposeResources = (this && this.__disposeResources) || (function (SuppressedError) {
    return function (env) {
        function fail(e) {
            env.error = env.hasError ? new SuppressedError(e, env.error, "An error was suppressed during disposal.") : e;
            env.hasError = true;
        }
        var r, s = 0;
        function next() {
            while (r = env.stack.pop()) {
                try {
                    if (!r.async && s === 1) return s = 0, env.stack.push(r), Promise.resolve().then(next);
                    if (r.dispose) {
                        var result = r.dispose.call(r.value);
                        if (r.async) return s |= 2, Promise.resolve(result).then(next, function(e) { fail(e); return next(); });
                    }
                    else s |= 1;
                }
                catch (e) {
                    fail(e);
                }
            }
            if (s === 1) return env.hasError ? Promise.reject(env.error) : Promise.resolve();
            if (env.hasError) throw env.error;
        }
        return next();
    };
})(typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
});
import { VNode } from './vnode.js';
/**
 * The VFS-level cache of vnodes for a single file system. Modeled after Linux's dcache/icache.
 *
 * Vnodes are keyed by inode number, so hard links share a vnode.
 * A vnode stays cached while it is referenced or dirty, and is evicted otherwise.
 * @category VFS
 * @todo Add LRU or something to limit memory usage
 */
export class VCache {
    fs;
    byIno = new Map();
    byPath = new Map();
    constructor(fs) {
        this.fs = fs;
    }
    /** Get the vnode for a path, if one is cached. Note paths are relative to the FS root, not the VFS root */
    get(path) {
        return this.byPath.get(path);
    }
    /**
     * Get or create the vnode for `path`, incrementing its reference count.
     * Callers must `unref` the vnode when done with it.
     */
    ref(path, inode) {
        let node = this.byIno.get(inode.ino);
        if (!node) {
            node = new VNode(this.fs, path, inode);
            this.byIno.set(inode.ino, node);
        }
        else {
            node.paths.add(path);
            // The vnode's inode is authoritative when dirty; otherwise refresh it with the newer stats
            if (!node.dirty && inode !== node.inode)
                Object.assign(node.inode, inode);
        }
        this.byPath.set(path, node);
        node.refs++;
        return node;
    }
    /** Release a reference to a vnode, evicting it if it is unreferenced and clean */
    unref(node) {
        node.refs--;
        if (node.refs > 0 || node.dirty)
            return;
        this.evict(node);
    }
    evict(node) {
        this.byIno.delete(node.ino);
        for (const path of node.paths)
            this.byPath.delete(path);
    }
    /** Update cached paths for a rename. Handles directories, whose descendants' paths change too */
    rename(oldPath, newPath) {
        for (const [path, node] of this.byPath) {
            if (path != oldPath && !path.startsWith(oldPath + '/'))
                continue;
            const moved = newPath + path.slice(oldPath.length);
            this.byPath.delete(path);
            this.byPath.set(moved, node);
            node.paths.delete(path);
            node.paths.add(moved);
        }
    }
    /** Add a path for a newly created hard link, if the target is cached */
    link(targetPath, linkPath) {
        const node = this.byPath.get(targetPath);
        if (!node)
            return;
        node.paths.add(linkPath);
        this.byPath.set(linkPath, node);
    }
    /** Remove a path for an unlink/rmdir. The vnode is kept if other paths (hard links) refer to it */
    remove(path) {
        const node = this.byPath.get(path);
        if (!node)
            return;
        this.byPath.delete(path);
        node.paths.delete(path);
        if (!node.paths.size && !node.refs)
            this.evict(node);
    }
    /** Sync all dirty vnodes to the backend */
    async sync() {
        for (const node of this.byIno.values()) {
            const env_1 = { stack: [], error: void 0, hasError: false };
            try {
                if (!node.dirty)
                    continue;
                const _ = __addDisposableResource(env_1, await node.lock('rw'), false);
                await node.sync();
                if (!node.refs)
                    this.evict(node);
            }
            catch (e_1) {
                env_1.error = e_1;
                env_1.hasError = true;
            }
            finally {
                __disposeResources(env_1);
            }
        }
    }
    /** Sync all dirty vnodes to the backend synchronously */
    syncSync() {
        for (const node of this.byIno.values()) {
            const env_2 = { stack: [], error: void 0, hasError: false };
            try {
                if (!node.dirty)
                    continue;
                const _ = __addDisposableResource(env_2, node.lockSync('rw'), false);
                node.syncSync();
                if (!node.refs)
                    this.evict(node);
            }
            catch (e_2) {
                env_2.error = e_2;
                env_2.hasError = true;
            }
            finally {
                __disposeResources(env_2);
            }
        }
    }
}
/**
 * All vnode caches, keyed by file system UUID.
 * Keyed by UUID rather than the `FileSystem` object because `resolveMount`
 * wraps file systems in a new exception-context proxy on every call.
 * @category VFS
 * @internal
 */
export const caches = new Map();
/**
 * Get the vnode cache for a file system, creating it if needed.
 * @category VFS
 * @internal
 */
export function cacheOf(fs) {
    let cache = caches.get(fs.uuid);
    if (!cache) {
        cache = new VCache(fs);
        caches.set(fs.uuid, cache);
    }
    return cache;
}
/**
 * Ref and lock the vnode for a path, usually a parent directory during a namespace operation.
 * The returned release function unlocks and unrefs the vnode.
 * If `inode` is not provided and the vnode is not cached, the backend is `stat`ed.
 * @category VFS
 * @internal
 */
export async function lockPath(fs, path, mode, inode) {
    const cache = cacheOf(fs);
    inode ??= cache.get(path)?.inode ?? (await fs.stat(path));
    const node = cache.ref(path, inode);
    const unlock = await node.lock(mode);
    let released = false;
    const release = () => {
        if (released)
            return;
        released = true;
        unlock();
        cache.unref(node);
    };
    release[Symbol.dispose] = release;
    return release;
}
/**
 * Synchronous version of `lockPath`.
 * @category VFS
 * @internal
 */
export function lockPathSync(fs, path, mode, inode) {
    const cache = cacheOf(fs);
    inode ??= cache.get(path)?.inode ?? fs.statSync(path);
    const node = cache.ref(path, inode);
    let unlock;
    try {
        unlock = node.lockSync(mode);
    }
    catch (e) {
        cache.unref(node);
        throw e;
    }
    let released = false;
    const release = () => {
        if (released)
            return;
        released = true;
        unlock();
        cache.unref(node);
    };
    release[Symbol.dispose] = release;
    return release;
}
